import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, BookOpen } from "lucide-react";
import { scrollToId } from "@/components/Header";
import { IMAGES } from "@/lib/images";

export default function HeroSection({ content }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const lines = (content.hero_lines || "").split("\n").filter(Boolean);

  return (
    <section ref={ref} id="top" className="relative min-h-screen blueprint-grid grain overflow-hidden" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-amber-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-red-600/5 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-24 grid lg:grid-cols-12 gap-14 items-center relative">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="font-mono-tactical text-xs uppercase tracking-[0.25em] text-amber-500"
            data-testid="hero-kicker"
          >
            {content.hero_kicker}
          </motion.p>

          <h1 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04]" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-amber-500" : "text-[#F3F4F6]"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.3 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed"
            data-testid="hero-sub"
          >
            {content.hero_sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("inquiry")}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0D0E11] font-bold px-7 py-3.5 rounded-full transition-all hover:gap-3"
              data-testid="hero-book-training-btn"
            >
              Book a Facility Training <ArrowUpRight size={18} />
            </button>
            <a
              href={content.amazon_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-amber-500/50 text-[#F3F4F6] hover:text-amber-500 px-7 py-3.5 rounded-full transition-colors"
              data-testid="hero-get-book-btn"
            >
              <BookOpen size={18} /> Get the Book
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-12 flex items-center gap-6 font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280]"
            data-testid="hero-credentials"
          >
            <span>AUTHOR</span>
            <span className="text-amber-500">◆</span>
            <span>SPEAKER</span>
            <span className="text-amber-500">◆</span>
            <span>STRATEGIST</span>
            <span className="text-amber-500">◆</span>
            <span>LIVED IT</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={{ y }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="absolute -inset-3 border border-amber-500/30 spotlight-frame pointer-events-none" />
            <img
              src={IMAGES.heroPortrait}
              alt="Angel Garcia — author of The Hood's Curriculum"
              className="w-full aspect-[4/5] object-cover spotlight-frame grayscale-[35%] contrast-110"
              data-testid="hero-portrait"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E11] via-transparent to-transparent spotlight-frame pointer-events-none" />
          </div>
          <div className="mt-4 flex items-center justify-between font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280]">
            <span data-testid="hero-portrait-caption">ANGEL GARCIA — FOUNDER</span>
            <span className="text-amber-500">S2S / 001</span>
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToId("manifesto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#6B7280] hover:text-amber-500 transition-colors animate-pulse-amber"
        aria-label="Scroll down"
        data-testid="hero-scroll-cue"
      >
        <ArrowDown size={20} />
      </motion.button>
    </section>
  );
}
